
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Html5QrcodeScanner } from 'html5-qrcode';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import pb from '../../lib/pocketbase';
import styles from './Inventory.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faTools, faBoxOpen, faExclamationTriangle, faQrcode, faSearch, faUserCheck, faMinus, faFileExcel, faHistory, faTimes, faPrint, faChartPie, faCalendarAlt, faWrench, faCheckCircle, faUndo, faClock, faImages, faTruck, faExchangeAlt, faTabletAlt, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import InventoryForm from '../../components/dashboard/InventoryForm';

const Inventory = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);

    // Modal State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isTransferOpen, setIsTransferOpen] = useState(false);
    const [transferItem, setTransferItem] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState('Dépôt');

    // Filter States
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('ALL'); // ALL, OUTIL, MATERIEL
    const [filterStock, setFilterStock] = useState('ALL'); // ALL, LOW, OK
    const [filterAssigned, setFilterAssigned] = useState('ALL'); // ALL, ASSIGNED, AVAILABLE
    const [filterCategory, setFilterCategory] = useState('ALL');
    const [viewMode, setViewMode] = useState('TABLE'); // TABLE, GRID
    const [historyItem, setHistoryItem] = useState(null);
    const [labelItem, setLabelItem] = useState(null);
    const [showStats, setShowStats] = useState(false);
    const [reservationItem, setReservationItem] = useState(null);
    const [maintenanceItem, setMaintenanceItem] = useState(null);
    const [kioskMode, setKioskMode] = useState(false);
    const [kioskAction, setKioskAction] = useState(null); // 'TAKE', 'RETURN'
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [reservationData, setReservationData] = useState({
        user: '',
        project: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: ''
    });

    useEffect(() => {
        loadInventory();
        loadFormData();
    }, []);

    const loadFormData = async () => {
        try {
            const [usersList, projectsList, suppliersList] = await Promise.all([
                pb.collection('users').getFullList({ sort: 'name' }),
                pb.collection('chantiers').getFullList({ sort: 'name' }),
                pb.collection('suppliers').getFullList({ sort: 'name' }).catch(() => [])
            ]);
            setUsers(usersList);
            setProjects(projectsList);
            setSuppliers(suppliersList);
        } catch (err) {
            console.error("Error loading reservation form data:", err);
        }
    };

    const loadInventory = async () => {
        try {
            const records = await pb.collection('inventory').getFullList({
                sort: 'name',
                expand: 'assigned_to'
            });
            setItems(records);
        } catch (err) {
            console.error("Erreur chargement inventaire", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingItem(null);
        setIsFormOpen(true);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cet article du stock ?")) return;
        try {
            await pb.collection('inventory').delete(id);
            toast.success("Article supprimé");
            loadInventory();
        } catch (err) {
            toast.error("Erreur à la suppression");
        }
    };

    const adjustQuantity = async (id, delta) => {
        const item = items.find(i => i.id === id);
        if (!item) return;

        const newQuantity = Math.max(0, item.quantity + delta);
        if (newQuantity === item.quantity) return;

        try {
            const now = new Date().toISOString();
            const logEntry = `[${now}] Ajustement: ${delta > 0 ? '+' : ''}${delta} (Nouveau: ${newQuantity})`;

            const currentLogs = item.logs || [];

            await pb.collection('inventory').update(id, {
                quantity: newQuantity,
                logs: [...currentLogs, logEntry].slice(-20) // Keep last 20 logs
            });
            // Update local state for immediate feedback
            setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: newQuantity, logs: [...(i.logs || []), logEntry].slice(-20) } : i));
        } catch (err) {
            toast.error("Erreur mise à jour stock");
        }
    };

    const handleSave = () => {
        setIsFormOpen(false);
        loadInventory();
    };

    const handleReserve = async () => {
        if (!reservationData.user || !reservationData.startDate) {
            toast.error("Veuillez remplir les champs obligatoires");
            return;
        }

        try {
            const newRes = {
                item: reservationItem.id,
                user: reservationData.user,
                project: reservationData.project || null,
                startDate: reservationData.startDate,
                endDate: reservationData.endDate || null,
                notes: reservationData.notes,
                status: 'PLANNED'
            };

            const now = new Date().toISOString();
            const today = now.split('T')[0];
            const userName = users.find(u => u.id === newRes.user)?.name || 'Utilisateur';

            let logEntry = `[${now}] RÉSERVATION: Prévue pour ${userName} du ${newRes.startDate}`;
            let updateData = {};

            // If start date is today, treat as immediate "Sortie"
            if (newRes.startDate === today) {
                logEntry = `[${now}] SORTIE: Attribué à ${userName} (Chantier: ${projects.find(p => p.id === newRes.project)?.name || 'Aucun'})`;
                updateData = {
                    assigned_to: newRes.user,
                    logs: [...(reservationItem.logs || []), logEntry].slice(-20)
                };

                // Generate PDF
                const userObj = users.find(u => u.id === newRes.user);
                const projectObj = projects.find(p => p.id === newRes.project);
                generateDeliverySlip(reservationItem, userObj, projectObj, newRes.notes);
            } else {
                updateData = {
                    logs: [...(reservationItem.logs || []), logEntry].slice(-20)
                };
            }

            await pb.collection('inventory').update(reservationItem.id, updateData);

            toast.success(newRes.startDate === today ? `Sortie validée pour ${reservationItem.name}` : `Réservation enregistrée`);
            setReservationItem(null);
            loadInventory();
        } catch (err) {
            toast.error("Erreur lors de la réservation");
            console.error(err);
        }
    };

    const handleRecordMaintenance = async (notes) => {
        try {
            const now = new Date().toISOString();
            const logEntry = `[${now}] MAINTENANCE: Réalisée. Notes: ${notes || 'Aucune'}`;

            await pb.collection('inventory').update(maintenanceItem.id, {
                last_maintenance: now,
                logs: [...(maintenanceItem.logs || []), logEntry].slice(-20)
            });

            toast.success(`Maintenance enregistrée pour ${maintenanceItem.name}`);
            setMaintenanceItem(null);
            loadInventory();
        } catch (err) {
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleReturn = async (item) => {
        if (!item.assigned_to) return;

        try {
            const now = new Date().toISOString();
            const userName = item.expand?.assigned_to?.username || 'Utilisateur';
            const logEntry = `[${now}] RETOUR: Article rendu par ${userName}`;

            await pb.collection('inventory').update(item.id, {
                assigned_to: null,
                logs: [...(item.logs || []), logEntry].slice(-20)
            });

            toast.success(`${item.name} est à présent disponible`);
            loadInventory();
        } catch (err) {
            toast.error("Erreur lors du retour matériel");
        }
    };

    const handleTransfer = async () => {
        if (!transferItem) return;

        try {
            const now = new Date().toISOString();
            const oldLocation = transferItem.location || 'Non spécifié';
            const logEntry = `[${now}] TRANSFERT: De ${oldLocation} vers ${selectedLocation}`;

            await pb.collection('inventory').update(transferItem.id, {
                location: selectedLocation,
                logs: [...(transferItem.logs || []), logEntry].slice(-20)
            });

            toast.success(`Transfert réussi vers ${selectedLocation}`);
            setIsTransferOpen(false);
            setTransferItem(null);
            loadInventory();
        } catch (err) {
            toast.error("Erreur lors du transfert");
            console.error(err);
        }
    };

    const handleScan = () => {
        setScanning(true);
    };

    useEffect(() => {
        if (scanning) {
            const scanner = new Html5QrcodeScanner(
                "reader",
                { fps: 10, qrbox: { width: 250, height: 250 } },
                false
            );

            const onScanSuccess = (decodedText, decodedResult) => {
                scanner.clear();
                setScanning(false);

                // Attempt to find item by ID or Name
                const found = items.find(i => i.id === decodedText || i.name.toLowerCase() === decodedText.toLowerCase());

                if (found) {
                    if (kioskMode) {
                        if (kioskAction === 'TAKE') {
                            setReservationItem(found);
                            // Pre-fill for immediate take
                            setReservationData(prev => ({ ...prev, startDate: new Date().toISOString().split('T')[0] }));
                        } else if (kioskAction === 'RETURN') {
                            if (window.confirm(`Confirmer le retour de : ${found.name} ?`)) {
                                handleReturn(found);
                            }
                        } else {
                            toast.info(`Article scanné: ${found.name}`);
                        }
                    } else {
                        toast.success(`Article trouvé : ${found.name}`);
                        handleEdit(found);
                    }
                } else {
                    toast.error(`Aucun article trouvé pour le code : ${decodedText}`);
                }
            };

            const onScanFailure = (error) => {
                // console.warn(`Code scan error = ${error}`);
            };

            scanner.render(onScanSuccess, onScanFailure);

            return () => {
                scanner.clear().catch(error => {
                    console.error("Failed to clear html5QrcodeScanner. ", error);
                });
            };
        }
    }, [scanning, items, kioskMode, kioskAction]);

    const getCategoryBadge = (category) => {
        const config = {
            'ELECTRICITE': { label: 'Électricité', icon: '⚡', color: '#fef3c7', text: '#92400e' },
            'PLOMBERIE': { label: 'Plomberie', icon: '🚰', color: '#e0f2fe', text: '#075985' },
            'MACONNERIE': { label: 'Maçonnerie', icon: '🧱', color: '#ffedd5', text: '#9a3412' },
            'PEINTURE': { label: 'Peinture', icon: '🎨', color: '#fdf2f8', text: '#9d174d' },
            'SECURITE': { label: 'Sécurité', icon: '🦺', color: '#ecfdf5', text: '#065f46' },
            'OUTILLAGE': { label: 'Outillage', icon: '⚙️', color: '#f1f5f9', text: '#334155' },
            'CONSOMMABLES': { label: 'Consommables', icon: '📦', color: '#f3f4f6', text: '#374151' },
            'AUTRE': { label: 'Autre', icon: '📁', color: '#f8fafc', text: '#64748b' }
        };

        const item = config[category] || config['AUTRE'];

        return (
            <span style={{
                padding: '4px 10px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '700',
                backgroundColor: item.color,
                color: item.text,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
            }}>
                {item.icon} {item.label}
            </span>
        );
    };

    const generateDeliverySlip = (item, user, project, notes) => {
        const doc = new jsPDF();
        const date = new Date().toLocaleDateString('fr-FR');
        const time = new Date().toLocaleTimeString('fr-FR');

        // Header
        doc.setFontSize(22);
        doc.setTextColor(40, 40, 40);
        doc.text("BON DE SORTIE MATÉRIEL", 105, 20, null, null, "center");

        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text("Maison Quinard", 105, 30, null, null, "center");

        // Info Box
        doc.setDrawColor(200);
        doc.setFillColor(245, 247, 250);
        doc.rect(14, 45, 182, 40, 'FD');

        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(`DATE : ${date} à ${time}`, 20, 55);
        doc.text(`BÉNÉFICIAIRE : ${user?.name || 'Non spécifié'}`, 20, 65);
        doc.text(`CHANTIER / PROJET : ${project?.name || 'Non spécifié'}`, 20, 75);
        if (notes) doc.text(`NOTES : ${notes}`, 20, 85);

        // Table
        const tableColumn = ["Article / Matériel", "Quantité", "État / Remarques"];
        const tableRows = [
            [item.name, "1", "Bon état - Sortie Standard"]
        ];

        doc.autoTable({
            startY: 95,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [66, 133, 244], textColor: 255, fontSize: 11 },
            styles: { fontSize: 10, cellPadding: 6 },
        });

        // Signatures
        const finalY = doc.lastAutoTable.finalY + 20;

        doc.setFontSize(10);
        doc.text("Signature du Responsable :", 20, finalY);
        doc.rect(20, finalY + 5, 80, 40); // Stick box

        doc.text("Signature du Réceptionnaire :", 110, finalY);
        doc.rect(110, finalY + 5, 80, 40); // Stick box

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text("En signant ce document, le réceptionnaire s'engage à restituer le matériel en bon état.", 105, finalY + 55, null, null, "center");

        // Create Blob URL and open in new tab (or download)
        // doc.save(`Bon_Sortie_${item.name}_${date.replace(/\//g, '-')}.pdf`);
        window.open(doc.output('bloburl'), '_blank');
    };

    const exportToExcel = () => {
        // Simple CSV export since we don't have an Excel library installed
        const headers = ["Type", "Nom", "Quantité", "Emplacement", "Assigné à"];
        const rows = items.map(item => [
            item.type,
            item.name,
            item.quantity,
            item.location || "",
            item.expand?.assigned_to?.username || ""
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `inventaire_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("✅ Export CSV réussi !");
    };

    // Filtered Items
    const filteredItems = React.useMemo(() => {
        return items.filter(item => {
            // Search filter
            const matchesSearch = !searchQuery ||
                item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.location?.toLowerCase().includes(searchQuery.toLowerCase());

            // Type filter
            const matchesType = filterType === 'ALL' || item.type === filterType;

            // Stock filter
            const isLowStock = item.min_quantity && item.quantity <= item.min_quantity;
            const matchesStock = filterStock === 'ALL' ||
                (filterStock === 'LOW' && isLowStock) ||
                (filterStock === 'OK' && !isLowStock);

            // Assignment filter
            const isAssigned = !!item.assigned_to;
            const matchesAssigned = filterAssigned === 'ALL' ||
                (filterAssigned === 'ASSIGNED' && isAssigned) ||
                (filterAssigned === 'AVAILABLE' && !isAssigned);

            // Category filter
            const matchesCategory = filterCategory === 'ALL' || item.category === filterCategory;

            return matchesSearch && matchesType && matchesStock && matchesAssigned && matchesCategory;
        });
    }, [items, searchQuery, filterType, filterStock, filterAssigned, filterCategory]);

    // Statistics Calculation
    const stats = React.useMemo(() => {
        const categoryData = items.reduce((acc, item) => {
            const cat = item.category || 'AUTRE';
            acc[cat] = (acc[cat] || 0) + 1;
            return acc;
        }, {});

        const pieData = Object.keys(categoryData).map(name => ({
            name,
            value: categoryData[name]
        }));

        const barData = items
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, 10)
            .map(i => ({ name: i.name.substring(0, 15), quantité: i.quantity }));

        return {
            total: items.length,
            tools: items.filter(i => i.type === 'OUTIL').length,
            material: items.filter(i => i.type === 'MATERIEL').length,
            lowStock: items.filter(i => i.min_quantity && i.quantity <= i.min_quantity).length,
            assigned: items.filter(i => !!i.assigned_to).length,
            pieData,
            barData,
            recentReservations: items.flatMap(item =>
                (item.logs || [])
                    .filter(log => log.includes('RÉSERVATION:'))
                    .map(log => ({
                        itemName: item.name,
                        details: log.split('RÉSERVATION:')[1].trim()
                    }))
            ).slice(0, 5),
            maintenanceAlerts: items.filter(i => {
                if (!i.last_maintenance || !i.maintenance_interval) return false;
                const last = new Date(i.last_maintenance);
                const next = new Date(last.getTime() + (i.maintenance_interval * 24 * 60 * 60 * 1000));
                return next < new Date();
            }).length,
            overdueReturns: items.filter(item => {
                if (!item.assigned_to) return false;
                // Simple logic: if a log has a planned end date that is passed
                const returnLog = (item.logs || []).find(l => l.includes('JUSQU\'AU:'));
                if (returnLog) {
                    const datePart = returnLog.split('JUSQU\'AU:')[1].trim().split(' ')[0];
                    return new Date(datePart) < new Date();
                }
                return false;
            }).length,
            planning: Array.from({ length: 7 }, (_, i) => {
                const date = new Date();
                date.setDate(date.getDate() + i);
                const dateStr = date.toISOString().split('T')[0];
                return {
                    date: dateStr,
                    dayName: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
                    reservations: items.flatMap(item =>
                        (item.logs || [])
                            .filter(log => log.includes('RÉSERVATION:') && log.includes(dateStr))
                            .map(log => ({
                                itemName: item.name,
                                user: log.split('pour ')[1]?.split(' du')[0] || '?'
                            }))
                    )
                };
            })
        };
    }, [items]);

    const COLORS = ['#3b82f6', '#8b5cf6', '#ef4444', '#10b981', '#f59e0b', '#34d399', '#60a5fa', '#a78bfa'];

    const handleGenerateOrder = () => {
        const lowStockItems = items.filter(i => i.min_quantity && i.quantity <= i.min_quantity);
        if (lowStockItems.length === 0) {
            toast.info("Aucun article en rupture de stock.");
            return;
        }

        // Group by supplier
        const ordersBySupplier = lowStockItems.reduce((acc, item) => {
            const supplierId = item.supplier || 'unknown';
            if (!acc[supplierId]) acc[supplierId] = [];
            acc[supplierId].push(item);
            return acc;
        }, {});

        Object.keys(ordersBySupplier).forEach(supplierId => {
            const supplier = suppliers.find(s => s.id === supplierId);
            const supplierName = supplier ? supplier.name : 'Fournisseur Inconnu';
            const supplierItems = ordersBySupplier[supplierId];

            const orderText = `COMMANDE POUR: ${supplierName}\n\n` +
                supplierItems.map(i => `- ${i.name}: Commander ${Math.max(10, (i.min_quantity * 2) - i.quantity)} unités`).join('\n');

            // In a real app, this would generate a PDF or send an email
            console.log(orderText);
            toast.success(`Commande générée pour ${supplierName} (${supplierItems.length} articles)`);
            alert(orderText); // Simple visual feedback for now
        });
    };

    if (kioskMode) {
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                backgroundColor: '#0f172a', zIndex: 9999, color: 'white',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
            }}>
                <button
                    onClick={() => setKioskMode(false)}
                    style={{
                        position: 'absolute', top: '20px', right: '20px',
                        background: 'transparent', border: '2px solid rgba(255,255,255,0.2)',
                        color: 'white', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer'
                    }}
                >
                    <FontAwesomeIcon icon={faSignOutAlt} /> Quitter Mode Borne
                </button>

                <h1 style={{ fontSize: '3rem', marginBottom: '4rem', fontWeight: '800', letterSpacing: '-1px' }}>
                    <span style={{ color: '#3b82f6' }}>Maison</span> Quinard
                </h1>

                <div style={{ display: 'flex', gap: '40px', width: '100%', maxWidth: '900px', padding: '0 20px' }}>
                    <button
                        onClick={() => {
                            setKioskAction('TAKE');
                            handleScan();
                        }}
                        style={{
                            flex: 1, padding: '60px 20px', borderRadius: '24px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none', color: 'white', cursor: 'pointer',
                            boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.3)',
                            transition: 'transform 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: '4rem', marginBottom: '20px', display: 'block' }} />
                        <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>JE PRENDS</span>
                        <p style={{ opacity: 0.8, marginTop: '10px' }}>Sortie de matériel / Consommables</p>
                    </button>

                    <button
                        onClick={() => {
                            setKioskAction('RETURN');
                            handleScan();
                        }}
                        style={{
                            flex: 1, padding: '60px 20px', borderRadius: '24px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            border: 'none', color: 'white', cursor: 'pointer',
                            boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.3)',
                            transition: 'transform 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <FontAwesomeIcon icon={faUndo} style={{ fontSize: '4rem', marginBottom: '20px', display: 'block' }} />
                        <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>JE RENDS</span>
                        <p style={{ opacity: 0.8, marginTop: '10px' }}>Retour de matériel au dépôt</p>
                    </button>
                </div>

                {/* Re-use existing modals if needed, specifically the scanning one logic happens in background or we render it on top */}
                {scanning && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 10000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{ width: '90%', maxWidth: '500px', background: 'white', padding: '20px', borderRadius: '16px' }}>
                            <button
                                onClick={() => setScanning(false)}
                                style={{ float: 'right', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                            <h3 style={{ color: 'black', textAlign: 'center', marginBottom: '20px' }}>
                                {kioskAction === 'TAKE' ? 'Scanner pour PRENDRE' : 'Scanner pour RENDRE'}
                            </h3>
                            <div id="reader"></div>
                        </div>
                    </div>
                )}

                {
                    reservationItem && kioskAction === 'TAKE' && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10001,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'black'
                        }}>
                            <div style={{ background: 'white', padding: '30px', borderRadius: '20px', width: '90%', maxWidth: '600px' }}>
                                <h2 style={{ marginBottom: '20px' }}>Valider la sortie : {reservationItem.name}</h2>
                                <label style={{ display: 'block', marginBottom: '10px' }}>Qui êtes-vous ?</label>
                                <select
                                    value={reservationData.user}
                                    onChange={(e) => setReservationData({ ...reservationData, user: e.target.value })}
                                    style={{ width: '100%', padding: '15px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px', fontSize: '1.1rem' }}
                                >
                                    <option value="">Sélectionner votre nom</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>

                                <label style={{ display: 'block', marginBottom: '10px' }}>Pour quel chantier ? (Optionnel)</label>
                                <select
                                    value={reservationData.project}
                                    onChange={(e) => setReservationData({ ...reservationData, project: e.target.value })}
                                    style={{ width: '100%', padding: '15px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px', fontSize: '1.1rem' }}
                                >
                                    <option value="">Sélectionner un chantier</option>
                                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>

                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <button
                                        onClick={handleReserve}
                                        style={{ flex: 1, padding: '15px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1.2rem', fontWeight: 'bold' }}
                                    >
                                        VALIDER
                                    </button>
                                    <button
                                        onClick={() => setReservationItem(null)}
                                        style={{ flex: 1, padding: '15px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1.2rem', fontWeight: 'bold' }}
                                    >
                                        ANNULER
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

            </div>
        );
    }

    return (
        <div className={styles.dataSection}>
            <div className={styles.sectionHeader}>
                <h3>Stocks & Matériel</h3>
                <div className={styles.headerActions}>
                    <button
                        className={`${styles.actionButton} ${styles.statsButton}`}
                        onClick={() => setShowStats(!showStats)}
                        title="Statistiques Avancées"
                    >
                        <FontAwesomeIcon icon={faChartPie} /> Dashboard
                    </button>
                    <button
                        className={`${styles.actionButton} ${styles.exportButton}`}
                        onClick={exportToExcel}
                        title="Exporter en CSV"
                    >
                        <FontAwesomeIcon icon={faFileExcel} /> Exporter
                    </button>
                    <button
                        onClick={() => setKioskMode(true)}
                        className={styles.actionButton}
                        style={{ background: '#0f172a', color: 'white' }}
                        title="Activer Mode Borne (Tablette)"
                    >
                        <FontAwesomeIcon icon={faTabletAlt} /> Borne
                    </button>
                    <button className={`${styles.actionButton} ${styles.scanButton}`} onClick={handleScan}>
                        <FontAwesomeIcon icon={faQrcode} /> Scan
                    </button>
                    <button className={`${styles.actionButton} ${styles.addButton}`} onClick={handleCreate}>
                        <FontAwesomeIcon icon={faPlus} /> Ajouter Item
                    </button>
                </div>
            </div>

            {/* Critical Alerts Banner */}
            {(stats.lowStock > 0 || stats.maintenanceAlerts > 0 || stats.overdueReturns > 0) && (
                <div className={styles.alertBanner}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={styles.alertIcon} />
                    <div className={styles.alertContent}>
                        <strong className={styles.alertTitle}>Alertes Inventaire Nécessitant votre Attention :</strong>
                        <div className={styles.alertMyDetails}>
                            {stats.lowStock > 0 && <span style={{ color: '#c2410c' }}>⚠️ <strong>{stats.lowStock}</strong> articles en stock bas</span>}
                            {stats.maintenanceAlerts > 0 && <span style={{ color: '#b45309' }}>🔧 <strong>{stats.maintenanceAlerts}</strong> maintenances à prévoir</span>}
                            {stats.overdueReturns > 0 && <span style={{ color: '#991b1b' }}>⏰ <strong>{stats.overdueReturns}</strong> retours en retard</span>}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleGenerateOrder}
                            className={styles.alertActionBtn}
                            style={{ background: '#ea580c' }}
                        >
                            📦 Commander
                        </button>
                        <button
                            onClick={() => {
                                setFilterStock('LOW');
                                setShowStats(false);
                            }}
                            className={styles.alertActionBtn}
                        >
                            Voir Liste
                        </button>
                    </div>
                </div>
            )}

            {/* Global History Tab / Section */}
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={() => setViewMode('HISTORY')}
                    className={`${styles.viewToggleButton} ${viewMode === 'HISTORY' ? styles.active : ''}`}
                    style={{ background: viewMode === 'HISTORY' ? 'var(--color-primary)' : 'white', color: viewMode === 'HISTORY' ? 'white' : 'var(--color-text-light)' }}
                >
                    <FontAwesomeIcon icon={faHistory} style={{ marginRight: '8px' }} /> Historique Global
                </button>
            </div>

            {/* Analytical Dashboard Section */}
            {showStats && (
                <div className={styles.statsDashboard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h4 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FontAwesomeIcon icon={faChartPie} style={{ color: '#8b5cf6' }} />
                            Analyse de l'Inventaire
                        </h4>
                        <button
                            onClick={() => setShowStats(false)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                        {/* Category Distribution Chart */}
                        <div className={styles.chartContainer}>
                            <p className={styles.chartTitle}>Distribution par Catégorie</p>
                            <div style={{ width: '100%', height: '250px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={stats.pieData}
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {stats.pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Top Stocked Items Chart */}
                        <div className={styles.chartContainer}>
                            <p className={styles.chartTitle}>Top 10 Stocks</p>
                            <div style={{ width: '100%', height: '250px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.barData}>
                                        <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="quantité" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Planned Reservations List */}
                        <div className={styles.chartContainer}>
                            <p className={styles.chartTitle}>Réservations Planifiées</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
                                {stats.recentReservations.length === 0 ? (
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center', py: '1rem' }}>Aucune réservation à venir</p>
                                ) : (
                                    stats.recentReservations.map((res, idx) => (
                                        <div key={idx} style={{
                                            padding: '10px',
                                            borderRadius: '8px',
                                            background: '#f0f9ff',
                                            borderLeft: '3px solid #3b82f6',
                                            fontSize: '0.85rem'
                                        }}>
                                            <strong style={{ color: '#1e40af' }}>{res.itemName}</strong>
                                            <div style={{ color: '#64748b', marginTop: '4px' }}>{res.details}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Weekly Planning Timeline */}
                        <div className={styles.chartContainer} style={{ gridColumn: '1 / -1' }}>
                            <p className={styles.chartTitle}>Planning de la Semaine (7 jours)</p>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                gap: '10px',
                                overflowX: 'auto'
                            }}>
                                {stats.planning.map((day, idx) => (
                                    <div key={idx} style={{
                                        background: idx === 0 ? '#f8fafc' : 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '10px',
                                        minHeight: '150px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        <div style={{ textAlign: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '5px' }}>
                                            <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>{day.dayName}</div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '800', color: idx === 0 ? '#3b82f6' : '#1e293b' }}>
                                                {new Date(day.date).getDate()}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', overflowY: 'auto' }}>
                                            {day.reservations.length === 0 ? (
                                                <div style={{ fontSize: '0.65rem', color: '#cbd5e1', textAlign: 'center', marginTop: '10px' }}>Libre</div>
                                            ) : (
                                                day.reservations.map((res, ridx) => (
                                                    <div key={ridx} style={{
                                                        fontSize: '0.7rem',
                                                        padding: '4px 6px',
                                                        background: '#eff6ff',
                                                        borderLeft: '2px solid #3b82f6',
                                                        borderRadius: '4px',
                                                        lineHeight: '1.2'
                                                    }}>
                                                        <div style={{ fontWeight: '700', color: '#1e40af' }}>{res.itemName}</div>
                                                        <div style={{ color: '#64748b', fontSize: '0.6rem' }}>{res.user}</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Grid */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard} style={{ borderLeftColor: '#3b82f6' }}>
                    <div className={styles.statIconBox} style={{ background: '#eff6ff', color: '#3b82f6' }}>
                        <FontAwesomeIcon icon={faTools} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Outils</div>
                        <div className={styles.statValue}>{stats.tools}</div>
                    </div>
                </div>

                <div className={styles.statCard} style={{ borderLeftColor: '#8b5cf6' }}>
                    <div className={styles.statIconBox} style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                        <FontAwesomeIcon icon={faBoxOpen} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Matériel</div>
                        <div className={styles.statValue}>{stats.material}</div>
                    </div>
                </div>

                <div className={styles.statCard} style={{ borderLeftColor: '#ef4444' }}>
                    <div className={styles.statIconBox} style={{ background: '#fef2f2', color: '#ef4444' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Stock Bas</div>
                        <div className={styles.statValue}>{stats.lowStock}</div>
                    </div>
                </div>

                <div className={styles.statCard} style={{ borderLeftColor: '#10b981' }}>
                    <div className={styles.statIconBox} style={{ background: '#ecfdf5', color: '#10b981' }}>
                        <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Assignés</div>
                        <div className={styles.statValue}>{stats.assigned}</div>
                    </div>
                </div>

                <div className={styles.statCard} style={{ borderLeftColor: '#f59e0b' }}>
                    <div className={styles.statIconBox} style={{ background: '#fff7ed', color: '#f59e0b' }}>
                        <FontAwesomeIcon icon={faWrench} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Maintenance</div>
                        <div className={styles.statValue}>{stats.maintenanceAlerts}</div>
                    </div>
                </div>

                <div className={styles.statCard} style={{ borderLeftColor: '#f87171' }}>
                    <div className={styles.statIconBox} style={{ background: '#fef2f2', color: '#f87171' }}>
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div>
                        <div className={styles.statLabel}>Retours en Retard</div>
                        <div className={styles.statValue}>{stats.overdueReturns}</div>
                    </div>
                </div>
            </div>

            {viewMode === 'HISTORY' ? (
                <div className={styles.tableContainer} style={{ padding: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>Historique Global de l'Activité</h4>
                        <button onClick={() => setViewMode('TABLE')} className={styles.viewToggleButton} style={{ background: '#f1f5f9' }}>Retour à la liste</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {items.flatMap(item => (item.logs || []).map(log => ({ item: item.name, log }))).sort((a, b) => b.log.localeCompare(a.log)).slice(0, 50).map((entry, idx) => (
                            <div key={idx} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                <div style={{ minWidth: '150px', fontSize: '0.8rem', color: '#64748b', fontWeight: 'bold' }}>
                                    {entry.log.substring(1, 20).replace('T', ' ')}
                                </div>
                                <div>
                                    <strong style={{ color: 'var(--color-primary)', fontSize: '0.9rem' }}>{entry.item}</strong>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>{entry.log.substring(21)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : <>
                < div className={styles.filterBar}>
                    <div className={styles.filterHeader}>
                        {/* Search Input */}
                        <div className={styles.searchContainer}>
                            <FontAwesomeIcon
                                icon={faSearch}
                                className={styles.searchIcon}
                            />
                            <input
                                type="search"
                                placeholder="Rechercher par nom ou emplacement..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        {/* View Toggles */}
                        <div className={styles.viewToggles}>
                            <button
                                onClick={() => setViewMode('TABLE')}
                                className={`${styles.viewToggleButton} ${viewMode === 'TABLE' ? styles.active : ''}`}
                            >
                                Liste
                            </button>
                            <button
                                onClick={() => setViewMode('GRID')}
                                className={`${styles.viewToggleButton} ${viewMode === 'GRID' ? styles.active : ''}`}
                            >
                                Cartes
                            </button>
                            <button
                                onClick={() => setViewMode('GALLERY')}
                                className={`${styles.viewToggleButton} ${viewMode === 'GALLERY' ? styles.active : ''}`}
                            >
                                <FontAwesomeIcon icon={faImages} /> Galerie
                            </button>
                            <button
                                onClick={() => setViewMode('CALENDAR')}
                                className={`${styles.viewToggleButton} ${viewMode === 'CALENDAR' ? styles.active : ''}`}
                            >
                                <FontAwesomeIcon icon={faCalendarAlt} /> Calendrier
                            </button>
                        </div>

                        {/* Type Filter */}
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className={styles.searchInput}
                            style={{ width: 'auto', cursor: 'pointer', fontWeight: '600' }}
                        >
                            <option value="ALL">Tous les types</option>
                            <option value="OUTIL">🔧 Outils</option>
                            <option value="MATERIEL">📦 Matériel</option>
                        </select>

                        {/* Stock Filter */}
                        <select
                            value={filterStock}
                            onChange={(e) => setFilterStock(e.target.value)}
                            className={styles.searchInput}
                            style={{ width: 'auto', cursor: 'pointer', fontWeight: '600' }}
                        >
                            <option value="ALL">Tous les stocks</option>
                            <option value="LOW">⚠️ Stock bas</option>
                            <option value="OK">✅ Stock OK</option>
                        </select>

                        {/* Assignment Filter */}
                        <select
                            value={filterAssigned}
                            onChange={(e) => setFilterAssigned(e.target.value)}
                            className={styles.searchInput}
                            style={{ width: 'auto', cursor: 'pointer', fontWeight: '600' }}
                        >
                            <option value="ALL">Tous</option>
                            <option value="ASSIGNED">👤 Assignés</option>
                            <option value="AVAILABLE">📍 Disponibles</option>
                        </select>

                        {/* Category Filter */}
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className={styles.searchInput}
                            style={{ width: 'auto', cursor: 'pointer', fontWeight: '600' }}
                        >
                            <option value="ALL">Toutes les catégories</option>
                            <option value="ELECTRICITE">⚡ Électricité</option>
                            <option value="PLOMBERIE">🚰 Plomberie</option>
                            <option value="MACONNERIE">🧱 Maçonnerie</option>
                            <option value="PEINTURE">🎨 Peinture</option>
                            <option value="SECURITE">🦺 Sécurité</option>
                            <option value="OUTILLAGE">⚙️ Outillage</option>
                            <option value="CONSOMMABLES">📦 Consommables</option>
                            <option value="AUTRE">📁 Autre</option>
                        </select>

                        {/* Results Count */}
                        <div style={{
                            marginLeft: 'auto',
                            fontSize: '0.9rem',
                            color: 'var(--color-text-light)',
                            fontWeight: '600'
                        }}>
                            {filteredItems.length} article{filteredItems.length > 1 ? 's' : ''}
                        </div>
                    </div>
                </div>

                {
                    loading ? (
                        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-light)' }}>
                            Chargement de l'inventaire...
                        </div>
                    ) : viewMode === 'GALLERY' ? (
                        <div className={styles.galleryGrid}>
                            {filteredItems.map(item => (
                                <div key={item.id} className={styles.galleryCard} onClick={() => handleEdit(item)}>
                                    <div className={styles.galleryImageContainer}>
                                        {item.image ? (
                                            <img
                                                src={pb.files.getURL(item, item.image)}
                                                alt={item.name}
                                                className={styles.galleryImage}
                                            />
                                        ) : (
                                            <FontAwesomeIcon icon={item.type === 'OUTIL' ? faTools : faBoxOpen} />
                                        )}
                                    </div>
                                    <div className={styles.galleryContent}>
                                        <div className={styles.galleryCategory}>{item.category}</div>
                                        <div className={styles.galleryTitle}>{item.name}</div>
                                        <div className={styles.galleryStatus}>
                                            <span style={{ fontWeight: 'bold', color: item.min_quantity && item.quantity <= item.min_quantity ? '#ef4444' : 'inherit' }}>
                                                Qté: {item.quantity}
                                            </span>
                                            {item.expand?.assigned_to ? (
                                                <span style={{ fontSize: '0.75rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px' }}>
                                                    {item.expand.assigned_to.username}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                    {item.location || 'Stock'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : viewMode === 'CALENDAR' ? (
                        <div className={styles.calendarContainer}>
                            <div className={styles.calendarHeader}>
                                <h3 style={{ margin: 0 }}>Calendrier Maintenance & Réservations</h3>
                                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Semaine du {new Date().toLocaleDateString()}</div>
                            </div>
                            <div className={styles.calendarGrid}>
                                {Array.from({ length: 7 }, (_, i) => {
                                    const date = new Date();
                                    date.setDate(date.getDate() + i);
                                    const dateStr = date.toISOString().split('T')[0];

                                    // Find reservations for this day
                                    const dailyReservations = items.flatMap(item =>
                                        (item.logs || [])
                                            .filter(log => log.includes('RÉSERVATION:') && log.includes(dateStr))
                                            .map(log => ({ item, log }))
                                    );

                                    // Find maintenance due around this day
                                    const maintenanceDue = items.filter(item => {
                                        if (!item.last_maintenance || !item.maintenance_interval) return false;
                                        const last = new Date(item.last_maintenance);
                                        const next = new Date(last.getTime() + (item.maintenance_interval * 24 * 60 * 60 * 1000));
                                        // Check if due date matches this calendar day
                                        return next.toISOString().split('T')[0] === dateStr;
                                    });

                                    return (
                                        <div key={i} className={styles.calendarDay}>
                                            <div className={styles.calendarDayHeader}>
                                                {date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                {dailyReservations.map((res, idx) => (
                                                    <div key={`res-${idx}`} style={{ fontSize: '0.7rem', padding: '4px', background: '#eff6ff', borderLeft: '2px solid #3b82f6', borderRadius: '2px' }}>
                                                        <strong>{res.item.name}</strong>
                                                    </div>
                                                ))}
                                                {maintenanceDue.map((item, idx) => (
                                                    <div key={`maint-${idx}`} style={{ fontSize: '0.7rem', padding: '4px', background: '#fff7ed', borderLeft: '2px solid #f97316', borderRadius: '2px', color: '#9a3412' }}>
                                                        🔧 {item.name}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : viewMode === 'GRID' ? (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '1.5rem'
                        }}>
                            {filteredItems.map((item) => {
                                const isLowStock = item.min_quantity && item.quantity <= item.min_quantity;
                                return (
                                    <div key={item.id} style={{
                                        background: 'white',
                                        borderRadius: 'var(--border-radius-lg)',
                                        padding: '1.5rem',
                                        boxShadow: 'var(--shadow-md)',
                                        border: isLowStock ? '2px solid #ef4444' : '1px solid var(--color-light-gray)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                        transition: 'transform 0.2s',
                                        cursor: 'pointer'
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                        onClick={() => handleEdit(item)}
                                    >
                                        {item.image && (
                                            <div style={{ width: '100%', height: '140px', marginBottom: '0.5rem', overflow: 'hidden', borderRadius: '8px' }}>
                                                <img
                                                    src={pb.files.getURL(item, item.image)}
                                                    alt={item.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                <div style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700',
                                                    background: item.type === 'OUTIL' ? '#eff6ff' : '#f1f5f9',
                                                    color: item.type === 'OUTIL' ? '#3b82f6' : '#475569',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}>
                                                    <FontAwesomeIcon icon={item.type === 'OUTIL' ? faTools : faBoxOpen} />
                                                    {item.type}
                                                </div>
                                                {getCategoryBadge(item.category)}
                                                {item.last_maintenance && item.maintenance_interval && (
                                                    (() => {
                                                        const last = new Date(item.last_maintenance);
                                                        const next = new Date(last.getTime() + (item.maintenance_interval * 24 * 60 * 60 * 1000));
                                                        if (next < new Date()) {
                                                            return (
                                                                <div style={{
                                                                    padding: '4px 10px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: '800',
                                                                    background: '#fef2f2',
                                                                    color: '#ef4444',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    border: '1px solid #fee2e2'
                                                                }}>
                                                                    <FontAwesomeIcon icon={faWrench} /> RÉVISION DOIT ÊTRE FAITE
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()
                                                )}
                                            </div>
                                            <div style={{ color: 'var(--color-text-light)', fontSize: '0.8rem' }}>
                                                {item.location || 'Sans emplacement'}
                                            </div>
                                        </div>

                                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>{item.name}</h4>

                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: isLowStock ? '#ef4444' : 'var(--color-text)' }}>
                                                    {item.quantity}
                                                </div>
                                                {isLowStock && <div style={{ color: '#ef4444', fontWeight: '700', fontSize: '0.7rem' }}>STOCK BAS</div>}
                                            </div>

                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <button
                                                    className={styles.edit}
                                                    onClick={(e) => { e.stopPropagation(); setMaintenanceItem(item); }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px', background: '#f59e0b' }}
                                                    title="Maintenance"
                                                >
                                                    <FontAwesomeIcon icon={faWrench} />
                                                </button>
                                                <button
                                                    className={styles.edit}
                                                    onClick={(e) => { e.stopPropagation(); setReservationItem(item); }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px', background: '#3b82f6' }}
                                                    title="Réserver"
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                                </button>
                                                <button
                                                    className={styles.edit}
                                                    onClick={(e) => { e.stopPropagation(); setLabelItem(item); }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px', background: '#10b981' }}
                                                    title="Générer Étiquette/QR"
                                                >
                                                    <FontAwesomeIcon icon={faQrcode} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTransferItem(item);
                                                        setSelectedLocation(item.location || 'Dépôt Principal');
                                                        setIsTransferOpen(true);
                                                    }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px', background: '#f59e0b', border: 'none', color: 'white', cursor: 'pointer' }}
                                                    title="Transférer"
                                                >
                                                    <FontAwesomeIcon icon={faExchangeAlt} />
                                                </button>
                                                <button
                                                    className={styles.edit}
                                                    onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px' }}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    className={styles.delete}
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                                    style={{ width: '30px', height: '30px', borderRadius: '4px' }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>

                                        {item.last_maintenance && (
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} />
                                                Dernière maintenance : {new Date(item.last_maintenance).toLocaleDateString()}
                                            </div>
                                        )}
                                        {item.expand?.assigned_to && (
                                            <div style={{
                                                borderTop: '1px solid var(--color-light-gray)',
                                                paddingTop: '0.8rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                fontSize: '0.85rem',
                                                color: 'var(--color-text-light)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <FontAwesomeIcon icon={faUserCheck} style={{ color: '#10b981' }} />
                                                    Assigné à <strong>{item.expand.assigned_to.username}</strong>
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleReturn(item); }}
                                                    style={{ border: 'none', padding: '5px 10px', borderRadius: '5px', background: '#f1f5f9', color: '#64748b', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                                >
                                                    <FontAwesomeIcon icon={faUndo} /> Retour
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {filteredItems.length === 0 && (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>
                                    {items.length === 0 ? "L'inventaire est vide." : "Aucun article ne correspond à vos filtres."}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.tableContainer}>
                            <table className={styles.inventoryTable}>
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th>Type</th>
                                        <th>Catégorie</th>
                                        <th>Nom</th>
                                        <th>Quantité</th>
                                        <th>Emplacement</th>
                                        <th>Assigné à</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map((item) => {
                                        const isLowStock = item.min_quantity && item.quantity <= item.min_quantity;

                                        return (
                                            <tr key={item.id} style={isLowStock ? { backgroundColor: '#fff7ed' } : {}}>
                                                <td style={{ width: '60px' }}>
                                                    {item.image ? (
                                                        <img
                                                            src={pb.files.getURL(item, item.image)}
                                                            alt=""
                                                            style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                                            <FontAwesomeIcon icon={item.type === 'OUTIL' ? faTools : faBoxOpen} />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {item.type === 'OUTIL' ? (
                                                        <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            <FontAwesomeIcon icon={faTools} /> Outil
                                                        </span>
                                                    ) : (
                                                        <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            <FontAwesomeIcon icon={faBoxOpen} /> Matériel
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{getCategoryBadge(item.category)}</td>
                                                <td style={{ fontWeight: 'bold' }}>{item.name}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <button
                                                            onClick={() => adjustQuantity(item.id, -1)}
                                                            style={{
                                                                width: '28px', height: '28px', borderRadius: '6px',
                                                                border: '1px solid var(--color-light-gray)',
                                                                background: 'white', cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                color: 'var(--color-text-light)', transition: 'all 0.2s'
                                                            }}
                                                            title="Diminuer"
                                                        >
                                                            <FontAwesomeIcon icon={faMinus} fontSize="0.75rem" />
                                                        </button>

                                                        <div style={{ textAlign: 'center', minWidth: '30px' }}>
                                                            <div style={{ fontSize: '1.1rem', fontWeight: '800', color: isLowStock ? '#ef4444' : 'var(--color-text)' }}>
                                                                {item.quantity}
                                                            </div>
                                                        </div>

                                                        <button
                                                            onClick={() => adjustQuantity(item.id, 1)}
                                                            style={{
                                                                width: '28px', height: '28px', borderRadius: '6px',
                                                                border: '1px solid var(--color-light-gray)',
                                                                background: 'white', cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                color: 'var(--color-text-light)', transition: 'all 0.2s'
                                                            }}
                                                            title="Augmenter"
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} fontSize="0.75rem" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>{item.location || '-'}</td>
                                                <td>
                                                    {item.expand?.assigned_to ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{
                                                                width: '24px', height: '24px', borderRadius: '50%',
                                                                backgroundColor: '#cbd5e1',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: '0.7rem', fontWeight: 'bold'
                                                            }}>
                                                                {item.expand.assigned_to.username?.charAt(0).toUpperCase()}
                                                            </div>
                                                            {item.expand.assigned_to.username}
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ display: 'flex', gap: '5px' }}>
                                                    {item.assigned_to && (
                                                        <button
                                                            onClick={() => handleReturn(item)}
                                                            style={{ background: '#64748b', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                            title="Confirmer le retour"
                                                        >
                                                            <FontAwesomeIcon icon={faUndo} />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setMaintenanceItem(item)}
                                                        style={{ background: '#f59e0b', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                        title="Maintenance"
                                                    >
                                                        <FontAwesomeIcon icon={faWrench} />
                                                    </button>
                                                    <button
                                                        onClick={() => setReservationItem(item)}
                                                        style={{ background: '#3b82f6', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                        title="Réserver"
                                                    >
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                    </button>
                                                    <button
                                                        onClick={() => setHistoryItem(item)}
                                                        style={{ background: '#64748b', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                        title="Historique"
                                                    >
                                                        <FontAwesomeIcon icon={faHistory} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        style={{ background: '#0f172a', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        style={{ background: '#ef4444', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </>
            }

            {
                isFormOpen && (
                    <InventoryForm
                        item={editingItem}
                        onSave={handleSave}
                        onCancel={() => setIsFormOpen(false)}
                    />
                )
            }

            {
                historyItem && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white', borderRadius: '15px',
                            width: '100%', maxWidth: '500px', padding: '2rem',
                            position: 'relative', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                        }}>
                            <button
                                onClick={() => setHistoryItem(null)}
                                style={{ position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <h3 style={{ margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <FontAwesomeIcon icon={faHistory} style={{ color: 'var(--color-primary)' }} />
                                Historique: {historyItem.name}
                            </h3>

                            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px' }}>
                                {(!historyItem.logs || historyItem.logs.length === 0) ? (
                                    <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Aucun mouvement enregistré.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {[...historyItem.logs].reverse().map((log, idx) => (
                                            <div key={idx} style={{
                                                padding: '12px',
                                                borderRadius: '8px',
                                                background: '#f8fafc',
                                                borderLeft: '4px solid var(--color-primary)',
                                                fontSize: '0.85rem'
                                            }}>
                                                {log}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {
                labelItem && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white', borderRadius: '15px',
                            width: '100%', maxWidth: '400px', padding: '2rem',
                            position: 'relative', textAlign: 'center'
                        }}>
                            <button
                                onClick={() => setLabelItem(null)}
                                style={{ position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <h3 style={{ marginBottom: '1.5rem' }}>Étiquette de l'article</h3>

                            <div id="printable-label" style={{
                                border: '2px solid black', padding: '20px',
                                display: 'inline-block', background: 'white',
                                color: 'black', marginBottom: '1.5rem'
                            }}>
                                <h2 style={{ margin: '0 0 10px 0', fontSize: '1.4rem' }}>{labelItem.name}</h2>
                                <p style={{ margin: '0 0 15px 0', fontSize: '0.9rem' }}>Loc: {labelItem.location || 'N/A'}</p>
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${labelItem.id}`}
                                    alt="QR Code"
                                    style={{ display: 'block', margin: '0 auto' }}
                                />
                                <p style={{ margin: '15px 0 0 0', fontStyle: 'italic', fontSize: '0.8rem' }}>ID: {labelItem.id}</p>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                <button
                                    onClick={() => window.print()}
                                    style={{ padding: '10px 20px', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                                >
                                    <FontAwesomeIcon icon={faPrint} /> Imprimer
                                </button>
                                <button
                                    onClick={() => setLabelItem(null)}
                                    style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                reservationItem && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white', borderRadius: '15px',
                            width: '100%', maxWidth: '500px', padding: '2rem',
                            position: 'relative'
                        }}>
                            <button
                                onClick={() => setReservationItem(null)}
                                style={{ position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#3b82f6' }} />
                                Réserver: {reservationItem.name}
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>Pour qui ? *</label>
                                    <select
                                        value={reservationData.user}
                                        onChange={(e) => setReservationData({ ...reservationData, user: e.target.value })}
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                                    >
                                        <option value="">Sélectionner un employé</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>Quel chantier ?</label>
                                    <select
                                        value={reservationData.project}
                                        onChange={(e) => setReservationData({ ...reservationData, project: e.target.value })}
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                                    >
                                        <option value="">Optionnel</option>
                                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>Début *</label>
                                        <input
                                            type="date"
                                            value={reservationData.startDate}
                                            onChange={(e) => setReservationData({ ...reservationData, startDate: e.target.value })}
                                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>Fin (prévue)</label>
                                        <input
                                            type="date"
                                            value={reservationData.endDate}
                                            onChange={(e) => setReservationData({ ...reservationData, endDate: e.target.value })}
                                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>Notes / Motif</label>
                                    <textarea
                                        value={reservationData.notes}
                                        onChange={(e) => setReservationData({ ...reservationData, notes: e.target.value })}
                                        placeholder="Ex: Utilisation pour la dalle du garage..."
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', minHeight: '80px' }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                                    <button
                                        onClick={handleReserve}
                                        style={{ flex: 1, padding: '12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                        Confirmer la réservation
                                    </button>
                                    <button
                                        onClick={() => setReservationItem(null)}
                                        style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                maintenanceItem && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white', borderRadius: '15px',
                            width: '100%', maxWidth: '450px', padding: '2rem',
                            position: 'relative', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                        }}>
                            <button
                                onClick={() => setMaintenanceItem(null)}
                                style={{ position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FontAwesomeIcon icon={faWrench} style={{ color: '#f59e0b' }} />
                                Enregistrer une Maintenance
                            </h3>

                            <div style={{ background: '#fff7ed', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #fed7aa' }}>
                                <p style={{ margin: 0, fontWeight: '700', color: '#9a3412' }}>{maintenanceItem.name}</p>
                                <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#c2410c' }}>
                                    Dernière fois : {maintenanceItem.last_maintenance ? new Date(maintenanceItem.last_maintenance).toLocaleDateString() : 'Jamais'}
                                </p>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>Notes / Intervention effectuée</label>
                                <textarea
                                    id="maintenance-notes"
                                    placeholder="Détails de l'intervention (ex: vidange, affûtage...)"
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', minHeight: '100px', marginBottom: '1.5rem' }}
                                />

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => handleRecordMaintenance(document.getElementById('maintenance-notes').value)}
                                        style={{ flex: 1, padding: '12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                        Valider l'intervention
                                    </button>
                                    <button
                                        onClick={() => setMaintenanceItem(null)}
                                        style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }


            {
                scanning && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 2000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{
                            background: 'white', borderRadius: '16px', padding: '20px',
                            width: '90%', maxWidth: '500px', position: 'relative'
                        }}>
                            <button
                                onClick={() => setScanning(false)}
                                style={{
                                    position: 'absolute', top: '10px', right: '10px',
                                    border: 'none', background: 'transparent',
                                    fontSize: '1.5rem', color: '#64748b', cursor: 'pointer', zIndex: 10
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Scanner un Code QR</h3>
                            <div id="reader" style={{ width: '100%' }}></div>
                            <p style={{ textAlign: 'center', color: '#64748b', marginTop: '15px', fontSize: '0.9rem' }}>
                                Placez le code QR de l'article face à la caméra.
                            </p>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Inventory;
